'use client';

import React from 'react';
import { useRegionPricing } from '@/features/payment/hooks/useRegionPricing';

export default function WhyWaitlistlySection() {
    const { priceText, isIndia, ready } = useRegionPricing();
    const savingsOver3Years = isIndia ? '₹40,000+' : '$500+';

    return (
        <section id="pricing" className="py-16 sm:py-24 bg-white">
            <div className="max-w-5xl mx-auto px-6 lg:px-8">
                {/* Pricing Comparison */}
                <div className="text-center mb-12">
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-4">
                        Stop paying monthly. <span className="text-violet-600">Forever.</span>
                    </h2>
                    <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
                        Other tools charge you every month. We charge once. You save forever.
                    </p>
                </div>

                {/* Two Column Comparison */}
                <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                    {/* Waitlistly Card - Lighter Purple */}
                    <div className="bg-violet-500 rounded-3xl p-8 text-white relative overflow-hidden">
                        <div className="absolute top-0 right-0 bg-emerald-400 text-emerald-900 text-xs font-bold px-4 py-1.5 rounded-bl-2xl">
                            BEST VALUE
                        </div>
                        <div className="mb-6">
                            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                                <span className="text-white font-bold text-lg">W</span>
                            </div>
                            <h3 className="font-bold text-xl">Waitlistly</h3>
                            <p className="text-violet-200 text-sm">The smarter choice</p>
                        </div>
                        <div className="mb-6">
                            <div className="flex items-baseline gap-2">
                                <span className={`text-5xl font-bold transition-opacity duration-300 ${ready ? 'opacity-100' : 'opacity-0'}`}>{priceText}</span>
                            </div>
                            <div className="text-violet-200 mt-1">per published site</div>
                        </div>
                        <div className="space-y-3 mb-8">
                            <FeatureCheck text="One-time payment" light />
                            <FeatureCheck text="Lifetime hosting included" light />
                            <FeatureCheck text="Unlimited email signups" light />
                            <FeatureCheck text="Custom domain support" light />
                            <FeatureCheck text="AI-powered copy generation" light />
                            <FeatureCheck text="Real-time analytics" light />
                            <FeatureCheck text="Built-in email automation" light />
                            <FeatureCheck text="Webhook integrations" light />
                        </div>
                        <div className="pt-6 border-t border-white/20">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                                    <svg className="w-5 h-5 text-emerald-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="font-semibold text-white">Risk-free</p>
                                    <p className="text-sm text-violet-200">Pay only when you&apos;re ready to publish</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Other Builders Card */}
                    <div className="bg-gray-50 rounded-3xl p-8 border border-gray-200 relative">
                        <div className="mb-6">
                            <div className="w-12 h-12 bg-gray-200 rounded-xl flex items-center justify-center mb-4">
                                <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                </svg>
                            </div>
                            <h3 className="font-bold text-xl text-gray-900">Other Builders</h3>
                            <p className="text-gray-500 text-sm">Landing page tools</p>
                        </div>
                        <div className="mb-6">
                            <div className="flex items-baseline gap-2">
                                <span className="text-5xl font-bold text-gray-900">~$50</span>
                                <span className="text-gray-500 font-medium">/month</span>
                            </div>
                            <div className="text-red-500 font-medium mt-1">≈ $600/year, every year</div>
                        </div>
                        <div className="space-y-3 mb-8">
                            <FeatureX text="Recurring monthly charges" />
                            <FeatureX text="Cancellation loses access" />
                            <FeatureX text="Price increases over time" />
                            <FeatureX text="No built-in growth tools" />
                            <FeatureX text="Hidden fees & upsells" />
                            <FeatureX text="Complex setup process" />
                            <FeatureX text="Overkill for validation" />
                        </div>
                        <div className="pt-6 border-t border-gray-200">
                            <div className="flex items-center gap-3 text-gray-400">
                                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-500">Subscription trap</p>
                                    <p className="text-sm text-gray-400">Bills every month forever</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Savings Highlight */}
                <div className="mt-8 bg-emerald-50 rounded-2xl p-6 border border-emerald-200 max-w-4xl mx-auto">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                                <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <div className="font-bold text-gray-900">Simple math, smart choice</div>
                                <div className="text-sm text-gray-600">Pay once, validate unlimited ideas</div>
                            </div>
                        </div>
                        <div className="text-center sm:text-right">
                            <div className={`text-2xl sm:text-3xl font-bold text-emerald-600 transition-opacity duration-300 ${ready ? 'opacity-100' : 'opacity-0'}`}>Save {savingsOver3Years}</div>
                            <div className="text-sm text-gray-500">over 3 years vs subscriptions</div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function FeatureCheck({ text, light }: { text: string; light?: boolean }) {
    return (
        <div className="flex items-center gap-3">
            <div className={`w-5 h-5 rounded-full flex items-center justify-center ${light ? 'bg-white/20' : 'bg-emerald-100'}`}>
                <svg className={`w-3 h-3 ${light ? 'text-emerald-300' : 'text-emerald-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
            </div>
            <span className={`${light ? 'text-white' : 'text-gray-700'}`}>{text}</span>
        </div>
    );
}

function FeatureX({ text }: { text: string }) {
    return (
        <div className="flex items-center gap-3">
            <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-3 h-3 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </div>
            <span className="text-gray-600">{text}</span>
        </div>
    );
}
